import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, KeyRound, Copy, Check, UserCog, Power, Info } from "lucide-react";

const ROLES = [
  { value: "master_admin", label: "Master Admin (Head Office)" },
  { value: "ho_staff", label: "Head Office Staff" },
  { value: "franchise_owner", label: "Franchise Owner" },
  { value: "franchise_team", label: "Franchise Team" },
  { value: "distributor_owner", label: "Distributor Owner" },
  { value: "distributor_team", label: "Distributor Team" },
];

const isFranchiseRole = (r) => r === "franchise_owner" || r === "franchise_team";
const isDistributorRole = (r) => r === "distributor_owner" || r === "distributor_team";

export default function Users() {
  const { user: me } = useAuth();
  const canEdit = me?.role === "master_admin";
  const [users, setUsers] = useState(null);
  const [franchises, setFranchises] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "franchise_team", franchise_id: "", distributor_id: "" });
  const [createdPw, setCreatedPw] = useState(null);
  const [editing, setEditing] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/franchises").then((r) => setFranchises(r.data));
    api.get("/distributors").then((r) => setDistributors(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name || !form.email || !form.role) return toast.error("All fields required");
    if (isFranchiseRole(form.role) && !form.franchise_id) return toast.error("Franchise is required for this role");
    if (isDistributorRole(form.role) && !form.distributor_id) return toast.error("Distributor is required for this role");
    try {
      const payload = {
        ...form,
        franchise_id: isFranchiseRole(form.role) ? form.franchise_id : null,
        distributor_id: isDistributorRole(form.role) ? form.distributor_id : null,
      };
      const { data } = await api.post("/users", payload);
      setCreatedPw({ email: data.email, password: data.temp_password });
      toast.success("User created");
      setShowCreate(false);
      setForm({ name: "", email: "", role: "franchise_team", franchise_id: "", distributor_id: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const update = async (id, patch) => {
    try {
      await api.patch(`/users/${id}`, patch);
      toast.success("Updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const resetPw = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}?`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setCreatedPw({ email: u.email, password: data.temp_password });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const copyPw = () => {
    navigator.clipboard.writeText(createdPw.password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-8" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2">Access Control</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A]">Users</h1>
          <p className="text-slate-500 mt-2">Create login accounts for head office and franchise teams.</p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowCreate(true)} className="bg-[#C9A84C] hover:bg-[#C9A84C]/90 text-white" data-testid="create-user-btn">
            <Plus className="w-4 h-4 mr-2" /> Add user
          </Button>
        )}
      </div>

      {!canEdit && (
        <div className="luxe-card p-4 bg-amber-50/50 border-l-4 border-amber-300 flex items-start gap-3" data-testid="readonly-banner">
          <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800">User list (read-only). Contact a Master Admin to manage users.</p>
        </div>
      )}

      <div className="luxe-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0D1B2A]/3">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-6 py-4">Name</th>
              <th className="px-6 py-4">Email</th>
              <th className="px-6 py-4">Role</th>
              <th className="px-6 py-4">Franchise / Distributor</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users === null && Array.from({ length: 3 }).map((_, i) => (
              <tr key={`skel-row-${i}`} className="border-t border-[#0D1B2A]/5"><td colSpan={6} className="p-6"><div className="skeleton h-6" /></td></tr>
            ))}
            {users && users.map((u) => {
              const role = ROLES.find((r) => r.value === u.role);
              return (
                <tr key={u.id} className="border-t border-[#0D1B2A]/5" data-testid={`user-row-${u.id}`}>
                  <td className="px-6 py-4 font-medium text-[#0D1B2A]">{u.name}</td>
                  <td className="px-6 py-4 text-slate-600">{u.email}</td>
                  <td className="px-6 py-4"><span className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#C9A84C]/10 text-[#8a6f1b] border border-[#C9A84C]/30">{role?.label || u.role}</span></td>
                  <td className="px-6 py-4 text-slate-600">{u.franchise_name || u.distributor_name || "—"}</td>
                  <td className="px-6 py-4">
                    <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full ${u.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                      {u.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {canEdit ? (
                      <div className="inline-flex gap-2">
                        <button onClick={() => setEditing(u)} className="p-2 rounded-md border border-[#0D1B2A]/15 text-slate-600 hover:bg-[#FAF8F3]" data-testid={`edit-user-${u.id}`}>
                          <UserCog className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button onClick={() => resetPw(u)} className="p-2 rounded-md border border-[#0D1B2A]/15 text-slate-600 hover:bg-[#FAF8F3]" data-testid={`reset-pw-${u.id}`}>
                          <KeyRound className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button onClick={() => update(u.id, { is_active: !u.is_active })} className="p-2 rounded-md border border-[#0D1B2A]/15 text-slate-600 hover:bg-[#FAF8F3]" data-testid={`toggle-active-${u.id}`}>
                          <Power className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Add user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="create-user-name" />
            <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="create-user-email" />
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="h-11 bg-white border-[#0D1B2A]/15" data-testid="create-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {isFranchiseRole(form.role) && (
              <Select value={form.franchise_id} onValueChange={(v) => setForm({ ...form, franchise_id: v })}>
                <SelectTrigger className="h-11 bg-white border-[#0D1B2A]/15" data-testid="create-user-franchise"><SelectValue placeholder="Select franchise…" /></SelectTrigger>
                <SelectContent>
                  {franchises.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {isDistributorRole(form.role) && (
              <Select value={form.distributor_id} onValueChange={(v) => setForm({ ...form, distributor_id: v })}>
                <SelectTrigger className="h-11 bg-white border-[#0D1B2A]/15" data-testid="create-user-distributor"><SelectValue placeholder="Select distributor…" /></SelectTrigger>
                <SelectContent>
                  {distributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={create} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="create-user-submit">Create user</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit modal */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Edit user</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <Select value={editing.role} onValueChange={(v) => setEditing({
                ...editing,
                role: v,
                franchise_id: isFranchiseRole(v) ? editing.franchise_id : null,
                distributor_id: isDistributorRole(v) ? editing.distributor_id : null,
              })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {isFranchiseRole(editing.role) && (
                <Select value={editing.franchise_id || ""} onValueChange={(v) => setEditing({ ...editing, franchise_id: v })}>
                  <SelectTrigger data-testid="edit-user-franchise"><SelectValue placeholder="Franchise" /></SelectTrigger>
                  <SelectContent>
                    {franchises.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {isDistributorRole(editing.role) && (
                <Select value={editing.distributor_id || ""} onValueChange={(v) => setEditing({ ...editing, distributor_id: v })}>
                  <SelectTrigger data-testid="edit-user-distributor"><SelectValue placeholder="Distributor" /></SelectTrigger>
                  <SelectContent>
                    {distributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => update(editing.id, {
              name: editing.name,
              role: editing.role,
              franchise_id: isFranchiseRole(editing.role) ? editing.franchise_id : null,
              distributor_id: isDistributorRole(editing.role) ? editing.distributor_id : null,
            })} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="edit-user-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp password reveal */}
      <Dialog open={!!createdPw} onOpenChange={(o) => { if (!o) setCreatedPw(null); }}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Temporary password</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Share this password with {createdPw?.email}. They will be required to change it on first login.</p>
          <div className="luxe-card p-4 flex items-center justify-between bg-[#FAF8F3]" data-testid="temp-password-display">
            <span className="font-mono text-xl text-[#0D1B2A]">{createdPw?.password}</span>
            <Button onClick={copyPw} variant="outline" className={copied ? "border-emerald-500 text-emerald-600" : "border-[#0D1B2A]/20"} data-testid="copy-temp-pw">
              {copied ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
